import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleQuoteClick = () => {
    const el = document.getElementById('get-quote') || document.getElementById('quote-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const target = document.getElementById('quote-form');
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const isActive = (path: string) => pathname === path;
  const textColor = scrolled ? 'text-gray-700' : 'text-white';
  const hoverColor = scrolled ? 'hover:text-teal-600' : 'hover:text-teal-300';
  const activeColor = scrolled ? 'text-teal-600 font-semibold' : 'text-teal-300 font-semibold';

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about', label: 'About Us' },
    { to: '/locations', label: 'Locations' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center space-x-12">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://cdn.gorilladash.com/static/clients/signarama/images/logos/signarama40th-new-logo.png"
                alt="Signarama Arlington"
                className={`h-12 w-auto transition-all duration-300 ${!scrolled ? 'brightness-0 invert' : ''}`}
              />
            </Link>
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isActive(link.to) ? activeColor : `${textColor} ${hoverColor}`
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <a
              href="tel:817-860-9310"
              className={`hidden md:flex items-center space-x-2 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${textColor}`}
            >
              <div className={`w-5 h-5 flex items-center justify-center`}>
                <i className="ri-phone-line text-lg"></i>
              </div>
              <span>817-860-9310</span>
            </a>
            <button
              onClick={handleQuoteClick}
              className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-semibold rounded-full hover:from-teal-600 hover:to-teal-700 transition-all whitespace-nowrap cursor-pointer"
            >
              Get A Quote
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden cursor-pointer ${textColor}`}
              aria-label="Toggle menu"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className={`lg:hidden py-4 border-t ${
              scrolled
                ? 'border-gray-200 bg-white'
                : 'border-white/20 bg-gray-900/95 backdrop-blur-md'
            }`}
          >
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block py-3 px-4 text-sm font-medium transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-300'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:817-860-9310"
              className={`flex items-center space-x-2 py-3 px-4 text-sm font-bold cursor-pointer ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              <i className="ri-phone-fill"></i>
              <span>817-860-9310</span>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
